import { useEffect, useState } from 'react'
import { Check, Copy, KeyRound, Loader2, MessageSquare, Plug, Save, Trash2, Webhook } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import {
  SupportWhatsAppService as Service,
  type SecretField, type WhatsAppSettings,
} from '@/services/modules/SupportWhatsAppService'
import { SupportSettingsService, type SupportTeam } from '@/services/modules/SupportSettingsService'

/**
 * Integração do atendimento com o WhatsApp (Meta Business).
 *
 * A tela existe para que a equipe que faz a integração preencha as credenciais
 * sem tocar no servidor. Os segredos entram mas não voltam: a API devolve só os
 * últimos dígitos, e deixar um campo em branco significa "não mexi neste
 * campo" — apagar de verdade é pelo botão de limpar.
 *
 * Ao salvar com o Phone Number ID, o canal é criado/atualizado na Central de
 * Atendimento, então ele já aparece no fluxo sem nenhum passo extra.
 */
export function SettingsSupportWhatsAppPage() {
  // PUT /settings/support/whatsapp e DELETE .../secret/{field} usam a mesma permissão.
  const { can } = useAuthority()
  const canManage = can('settings.support_whatsapp.manage')
  const [data, setData] = useState<WhatsAppSettings | null>(null)
  const [teams, setTeams] = useState<SupportTeam[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)

  // Segredos: campo vazio = manter o que já está gravado.
  const [token, setToken] = useState('')
  const [appSecret, setAppSecret] = useState('')
  const [verifyToken, setVerifyToken] = useState('')

  const [phoneNumberId, setPhoneNumberId] = useState('')
  const [wabaId, setWabaId] = useState('')
  const [phoneE164, setPhoneE164] = useState('')
  const [apiVersion, setApiVersion] = useState('v21.0')
  const [inboxName, setInboxName] = useState('WhatsApp')
  const [defaultTeamId, setDefaultTeamId] = useState('')

  function hydrate(s: WhatsAppSettings) {
    setData(s)
    setPhoneNumberId(s.phoneNumberId ?? '')
    setWabaId(s.wabaId ?? '')
    setPhoneE164(s.phoneE164 ?? '')
    setApiVersion(s.apiVersion || 'v21.0')
    setInboxName(s.inbox?.name ?? 'WhatsApp')
    setDefaultTeamId(s.inbox?.defaultTeam?.id ?? '')
    setToken(''); setAppSecret(''); setVerifyToken('')
  }

  useEffect(() => {
    Promise.all([
      Service.get(),
      SupportSettingsService.teams().catch(() => [] as SupportTeam[]),
    ])
      .then(([s, t]) => { hydrate(s); setTeams(t) })
      .catch((e) => toast.error({ title: 'Não foi possível carregar', description: extractApiError(e).message }))
      .finally(() => setLoading(false))
  }, [])

  async function save() {
    setSaving(true)
    try {
      hydrate(await Service.update({
        token: token.trim() || null,
        appSecret: appSecret.trim() || null,
        verifyToken: verifyToken.trim() || null,
        phoneNumberId: phoneNumberId.trim() || null,
        wabaId: wabaId.trim() || null,
        phoneE164: phoneE164.trim() || null,
        apiVersion: apiVersion.trim() || 'v21.0',
        inboxName: inboxName.trim() || null,
        defaultTeamId: defaultTeamId || null,
      }))
      toast.success('Integração salva.')
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  async function clear(field: SecretField) {
    try {
      hydrate(await Service.clearSecret(field))
      toast.success('Credencial removida.')
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    }
  }

  async function copyWebhook() {
    if (!data) return
    try {
      await navigator.clipboard.writeText(data.webhookUrl)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Não foi possível copiar. Selecione o texto manualmente.')
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  const sections: FormSection[] = [
    {
      key: 'situacao', label: 'Situação', icon: Plug,
      content: (
        <div className="rounded-md bg-white p-5 ring-1 ring-gray-200 sm:p-6">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">Situação da integração</span>
            <Badge variant={data?.live ? 'green' : 'amber'}>
              {data?.live ? 'no ar' : 'aguardando credenciais'}
            </Badge>
          </div>
          <p className="text-xs text-gray-500">{data?.statusText}</p>

          {(data?.missing.length ?? 0) > 0 && (
            <div className="mt-3 rounded-md bg-amber-50 px-3 py-2.5 text-xs text-amber-800 ring-1 ring-amber-200">
              <p className="mb-1 font-medium">Falta preencher:</p>
              <ul className="list-disc pl-4">
                {data!.missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
            </div>
          )}

          <p className="mt-3 text-[11px] text-gray-500">
            Não há chave de liga/desliga aqui: com token e número preenchidos o canal entra no ar
            sozinho e as mensagens passam a cair na Central de Atendimento. Para pausar sem apagar
            as credenciais, inative o canal em Configurações do atendimento → Canais.
          </p>
        </div>
      ),
    },
    {
      key: 'webhook', label: 'Webhook', icon: Webhook,
      content: (
        <Section
          icon={<Webhook size={15} />}
          title="Webhook"
          description="Endereço que a equipe de integração cadastra no Meta Business, junto com o Verify Token abaixo. Assine o campo messages para receber mensagens e status de entrega."
        >
          <div className="flex flex-wrap items-center gap-2">
            <code className="flex-1 break-all rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700 ring-1 ring-gray-200">
              {data?.webhookUrl}
            </code>
            <Button variant="secondary" size="sm" onClick={copyWebhook}
              iconLeft={copied ? <Check size={13} /> : <Copy size={13} />}>
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
        </Section>
      ),
    },
    {
      key: 'credenciais', label: 'Credenciais', icon: KeyRound,
      content: (
        <Section
          icon={<KeyRound size={15} />}
          title="Credenciais"
          description="Preenchidas pela equipe que faz a integração. Ficam criptografadas e nunca voltam para a tela — só os últimos dígitos, para conferência."
        >
          <div className="space-y-3">
            <SecretInput
              label="Token permanente" value={token} onChange={setToken}
              state={data?.secrets.token} onClear={canManage ? () => clear('token') : undefined}
              placeholder="Token permanente gerado na Meta" hint="Usado para enviar as mensagens."
            />
            <SecretInput
              label="App Secret" value={appSecret} onChange={setAppSecret}
              state={data?.secrets.appSecret} onClear={canManage ? () => clear('appSecret') : undefined}
              placeholder="Chave secreta do app" hint="Confere a assinatura de cada webhook recebido."
            />
            <SecretInput
              label="Verify Token" value={verifyToken} onChange={setVerifyToken}
              state={data?.secrets.verifyToken} onClear={canManage ? () => clear('verifyToken') : undefined}
              placeholder="Texto livre, definido por vocês"
              hint="Tem que ser exatamente o mesmo texto informado no Meta Business."
            />
          </div>
        </Section>
      ),
    },
    {
      key: 'numero', label: 'Número e conta', icon: MessageSquare,
      content: (
        <Section
          icon={<MessageSquare size={15} />}
          title="Número e conta"
          description="Identificadores do número na Meta. O Phone Number ID é o que liga cada mensagem recebida à caixa certa da central."
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input label="Phone Number ID" value={phoneNumberId}
              onChange={e => setPhoneNumberId(e.target.value)}
              placeholder="Ex.: 123456789012345"
              hint="Não é o telefone — é o id do número no painel da Meta." />
            <Input label="WABA ID" value={wabaId}
              onChange={e => setWabaId(e.target.value)}
              placeholder="Id da conta WhatsApp Business"
              hint="Usado para sincronizar os modelos aprovados." />
            <Input label="Telefone exibido" value={phoneE164}
              onChange={e => setPhoneE164(e.target.value)}
              placeholder="+5511999999999"
              hint="Apenas informativo, no formato internacional." />
            <Select label="Versão da API" value={apiVersion}
              onChange={e => setApiVersion(e.target.value)}
              options={['v23.0', 'v22.0', 'v21.0', 'v20.0']}
              clearable={false}
              hint="Mantenha na versão indicada pela equipe de integração." />
          </div>
        </Section>
      ),
    },
    {
      key: 'canal', label: 'Canal na central', icon: Plug,
      content: (
        <Section
          icon={<MessageSquare size={15} />}
          title="Canal na Central de Atendimento"
          description="Esta tela é a dona do canal de WhatsApp: ao salvar, ele é criado (ou atualizado) na Central com o número, o nome e a fila abaixo. Na aba Canais o número aparece apenas para conferência."
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input label="Nome do canal" required value={inboxName}
              onChange={e => setInboxName(e.target.value)}
              placeholder="WhatsApp"
              hint="Como o canal aparece na fila dos atendentes." />
            <Select label="Fila padrão" value={defaultTeamId}
              onChange={e => setDefaultTeamId(e.target.value)}
              options={teams.map(t => ({ value: t.id, label: t.name }))}
              placeholder="Sem fila padrão"
              hint="Chamados novos deste canal caem nesta equipe." />
          </div>

          <div className="mt-4 rounded-md bg-gray-50 px-3 py-2.5 text-xs ring-1 ring-gray-200">
            {data?.inbox ? (
              <span className="text-gray-700">
                Canal <strong>{data.inbox.name}</strong> ativo na central
                {data.inbox.defaultTeam ? <> — fila <strong>{data.inbox.defaultTeam.name}</strong></> : ' — sem fila padrão'}.
              </span>
            ) : (
              <span className="text-gray-500">
                Nenhum canal criado ainda. Preencha o Phone Number ID e salve para que ele apareça na central.
              </span>
            )}
          </div>
        </Section>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="WhatsApp (Meta Business)"
        subtitle="Credenciais da integração e vínculo do número com a Central de Atendimento"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Atendimento' }, { label: 'WhatsApp' }]}
        backTo="/settings"
        actions={(
          <Button onClick={save} loading={saving} iconLeft={<Save size={14} />}
            disabled={!canManage || (!!phoneNumberId.trim() && !inboxName.trim())}
            title={canManage ? undefined : 'Sem permissão para alterar a integração'}>
            Salvar
          </Button>
        )}
      />

      <FormSidemenu sections={sections} />
    </>
  )
}

function SecretInput({ label, value, onChange, state, onClear, placeholder, hint }: {
  label: string
  value: string
  onChange: (v: string) => void
  state?: { filled: boolean; hint: string | null; fromEnv: boolean }
  /** Ausente quando o usuário não pode limpar a credencial. */
  onClear?: () => void
  placeholder?: string
  hint?: string
}) {
  const saved = state?.filled
  const fromEnv = state?.fromEnv

  return (
    <div>
      <div className="flex flex-wrap items-end gap-2">
        <Input
          label={label}
          type="password"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={saved ? 'Preenchido — digite para substituir' : placeholder}
          wrapperClassName="flex-1"
          autoComplete="new-password"
        />
        {saved && onClear && (
          <Button variant="ghost" size="sm" onClick={onClear} iconLeft={<Trash2 size={13} />}>
            Limpar
          </Button>
        )}
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
        {saved && <Badge variant="green">salvo {state?.hint}</Badge>}
        {!saved && fromEnv && <Badge variant="gray">vindo do .env do servidor</Badge>}
        {!saved && !fromEnv && <Badge variant="amber">não configurado</Badge>}
        {hint}
      </p>
    </div>
  )
}

function Section({ icon, title, description, children }: {
  icon: React.ReactNode
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-md bg-white p-5 ring-1 ring-gray-200 sm:p-6">
      <h2 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
        <span className="text-brand-600">{icon}</span>{title}
      </h2>
      <p className="mb-4 text-xs text-gray-500">{description}</p>
      {children}
    </div>
  )
}
